import { useEffect, useState } from "react"
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { validateFile } from '@/utils/validateImage';
import { propostaApi } from "@/services/api/proposta";
import { IconButton } from "@mui/material";
import styles from "./EnviarImagensComponente.module.css"
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';

interface EnviarImagensProps {
    classContainerImagem?: string
    classDragAndDropArea?: string
    classDescricaoTitulo?: string
    classImagens?: string
    proposta_id: number
    enviar: boolean
    onChangeImagens?: (imagens: string[]) => void
    alteracaoManualRef?: React.MutableRefObject<boolean>;
    readonly?: boolean;
}

export function EnviarImagensComponente({ classContainerImagem, classDragAndDropArea, classDescricaoTitulo, classImagens, proposta_id, enviar, onChangeImagens, alteracaoManualRef, readonly }: EnviarImagensProps) {
    const [imagens, setImagens] = useState<File[]>([])
    const [imgURLs, setImgURLs] = useState<string[]>([])
    const [imagensExistentes, setImagensExistentes] = useState<
        { id: number; referencia: string }[]
    >([]);

    useEffect(() => {
        async function fetchExistingImages() {
            if (!proposta_id || proposta_id === 0) {
                setImagensExistentes([]);
                return;
            }

            try {
                const response = await propostaApi.listarImagemProposta(proposta_id.toString());
                const imagensProposta = response.data.imagemProposta || [];
                setImagensExistentes(imagensProposta);
            } catch (error) {
                console.error("Erro ao carregar imagens existentes:", error);
            }
        }

        fetchExistingImages();
    }, [proposta_id]);

    function handleDrop(event: React.DragEvent<HTMLLabelElement>) {
        if (readonly) return;
        console.log("drop: ", event)
        event.preventDefault()
        const files = event.dataTransfer.files
        const fakeEvent = {
            target: {
                files: files,
                value: ''
            } as HTMLInputElement
        } as React.ChangeEvent<HTMLInputElement>
        const validFiles = validateFile(fakeEvent)
        if (validFiles && validFiles.length) {
            setImagens(prev => [...prev, ...validFiles])
        }
    }

    function handleDragOver(event: React.DragEvent<HTMLLabelElement>) {
        event.preventDefault()
      }

    function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
        if (readonly) return;
        const newFiles = event.target.files

        if (newFiles) {
            const newImages: File[] = []
            const otherFiles: File[] = []

            for (let i = 0; i < newFiles.length; i++) {
                const file = newFiles[i]
                if (file.type.match('image.*')) {
                    newImages.push(file)
                } else if (file.type === 'application/pdf' || file.type.match('application/msword') || file.type.match('application/vnd.openxmlformats-officedocument.wordprocessingml.document')) {
                    otherFiles.push(file)
                }
            }
            if (newImages.length > 0) {
                if (alteracaoManualRef) {
                    alteracaoManualRef.current = true;
                  }                  
              }
            setImagens(prevImages => [...prevImages, ...newImages])
            event.target.value = '';
        }
    }

    async function enviarArquivo() {
        if (imagens.length > 0) {
            await propostaApi.criarImagemProposta(proposta_id.toString(), imagens);
        }
    }

    async function handleRemoveExistingImage(imageId: number) {
        try {
            await propostaApi.removerImagemProposta(imageId.toString());
            setImagensExistentes((prev) =>
                prev.filter((imagem) => imagem.id !== imageId)
            );
            if (alteracaoManualRef) {
                alteracaoManualRef.current = true;
              }              
        } catch (error) {
            console.error("Erro ao remover imagem existente:", error);
        }
    }

    async function reloadExistingImages() {
        try {
            const response = await propostaApi.listarImagemProposta(proposta_id.toString());
            const imagensProposta = response.data.imagemProposta || [];
            setImagensExistentes(imagensProposta);
        } catch (error) {
            console.error("Erro ao carregar imagens existentes:", error);
        }
    }    

    function handleRemoveNewImage(index: number) {
        setImgURLs((prevURLs) => {
            const updatedURLs = prevURLs.filter((_, i) => i !== index);
            onChangeImagens?.([
                ...imagensExistentes.map((img) => img.referencia),
                ...updatedURLs,
            ]); // Atualiza as imagens no preview
            return updatedURLs;
        });
    
        setImagens((prevImages) => prevImages.filter((_, i) => i !== index));
        if (alteracaoManualRef) {
            alteracaoManualRef.current = true;
          }          
    }    

    useEffect(() => {
        reloadExistingImages();
    }, [proposta_id]);    

    useEffect(() => {
        console.log("Imagens atualizadas: ", imagens)
    }, [imagens])

    useEffect(() => {
        if (onChangeImagens) {
            onChangeImagens(imgURLs)
        }
    }, [imgURLs])

    useEffect(() => {
        if (enviar && imagens.length > 0) {
            enviarArquivo()
                .then(() => {
                    setImagens([]); // Limpar imagens após o envio
                    setImgURLs([]); // Limpar URLs após o envio
                    reloadExistingImages();
                })
                .catch(error => {
                    console.error("Erro ao enviar imagens:", error);
                })
                .finally(() => {
                    onChangeImagens?.([]); // Notifica o componente pai que as imagens foram enviadas
                });
        }
    }, [enviar]);

    useEffect(() => {
        const newImgURLs = imagens.map((file) => URL.createObjectURL(file));
        
        setImgURLs((prevURLs) => [
            ...imagensExistentes.map((img) => img.referencia),
            ...newImgURLs,
        ]);
    
        // Cleanup para evitar vazamentos de memória
        return () => {
            newImgURLs.forEach(URL.revokeObjectURL);
        };
    }, [imagens, imagensExistentes]);
    
    return (
        <>
            {!readonly && (
            <div className={classContainerImagem}>
                <label
                    htmlFor="img-upload" className={classDragAndDropArea}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragOver}
                    onDrop={handleDrop}
                >
                    <div className={classDescricaoTitulo}>
                        <UploadFileIcon />
                        <span>Arraste ou selecione sua imagem</span>
                        <span>São permitidos arquivos com no máximo 10MB</span>
                    </div>

                    <input
                        id="img-upload"
                        type="file"
                        accept="image/jpeg, image/png, image/gif"
                        hidden
                        multiple
                        onChange={handleFileChange}
                    />
                </label>
            </div>
            )}

            <div className={classImagens}>

                {/* Renderizar imagens existentes */}
                {imagensExistentes.map((img) => (
                    <div key={img.id} className={styles.imagem_linha}>
                        <div className={styles.img_container}>
                            <img src={img.referencia} alt="Imagem existente" />
                        </div>
                        {!readonly && (
                            <IconButton
                                onClick={() => handleRemoveExistingImage(img.id)}
                            >
                                <CancelOutlinedIcon style={{ fontSize: "16px" }} />
                            </IconButton>
                        )}
                    </div>
                ))}

                {/* Renderizar imagens novas */}
                {imagens.map((file, index) => (
                    <div key={index} className={styles.imagem_linha}>
                        <div className={styles.img_container}>
                            <img src={URL.createObjectURL(file)} alt="Imagem nova" />
                            <div className={styles.image_info}>
                                <div>{file.name}</div>
                                <div className={styles.image_size}>
                                    {Math.round(file.size / 1024)} KB
                                </div>
                            </div>
                        </div>
                        {!readonly && (
                            <IconButton onClick={() => handleRemoveNewImage(index)}>
                                <CancelOutlinedIcon style={{ fontSize: "16px" }} />
                            </IconButton>
                        )}
                    </div>
                ))}
            </div>
        </>

    )
}